<template>
  <div class="ballot mt-3" :key="ballot.id">
    <!-- Ballot/VotingRound is closed -->
    <header class="h1 text-center">{{ title }}</header>
    <div class="lead" v-html="description" />

    <div v-if="!isBallotOpen && votingRound.contestReferences.length !== 1 && !votingRoundReport?.publishedAt"
      class="h5 mt-3">
      {{ votingRoundContests.length > 1 ? $t("js.ballot.ballots_in_slide") : $t("js.ballot.ballot_in_slide") }}
    </div>
    <div v-if="isIdentifiable && !isHandRaise" class="alert alert-info">
      <details>
        <summary class="h5 mb-0">
          {{ $t("js.ballot.anonymous_notice.header") }}
        </summary>
        <p class="mb-0">{{ $t("js.ballot.anonymous_notice.body") }}</p>
      </details>
    </div>

    <template v-if="showSupplementaryHeader">
      <ContestHeader v-for="contest in votingRoundContests" :show-description="ballot.state === 'open'"
        :contest="contest" :is-eligible="voter.canVoteOn.includes(contest.reference)" :locale="$i18n.locale" />
    </template>
    <hr>

    <template v-if="(votingRound.status === 'closed' && !isBallotOpen) || ballot.state === 'new'">
      <template v-if="ballot.state === 'finished'">
        <!-- If result is published -->
        <template v-if="!!votingRoundReport?.publishedAt">
          <template v-for="res in orchestrators">
            <template v-if="res.result">
              <Result :slide="ballot"
                :contest="contests.find((con: ConferenceContest) => con.reference === res.contestReference)"
                :result="res.result"
                :published="true"
                :voting-round-report="votingRoundReport"
                :header="contests.find((con: ConferenceContest) => con.reference === res.contestReference).title[$i18n.locale]"
              />
            </template>
          </template>
        </template>

        <!-- If result isn't published -->
        <div v-else class="p-3 text-center">
          <p class="mb-0">{{ $t("js.ballot.not_published") }}</p>
        </div>

      </template>
    </template>

    <!-- Ballot/VotingRound is open -->
    <template v-else>
      <!-- Can vote -->
      <template v-if="hasVotingAccess">
        <!-- Has already voted -->
        <template v-if="hasVoted">
          <BallotProgress :ballot="ballot" class="my-5" />
          <hr>
          <div class="my-5 text-center">
            <h4 class="text-success my-3"><AVIcon icon="check" class="me-3" />{{ $t("js.ballot.vote_registered") }}</h4>
            <h3 v-for="contest in accessibleContests" class="contest-title">{{ contest.title[$i18n.locale] }}</h3>
            <template v-if="handRaiseLiveResults">
              <AVBallot class="my-3" :contest="formatContest(votingRoundContests[0])"
                :partial-results="handRaiseLiveResults" :observer-mode="true" :selectionPile="mockSelectionPile" />
              <hr>
            </template>
            <a v-if="receipt && canDownloadReceipt" :href="receiptUrl" class="btn btn-theme my-3">
              <AVIcon icon="download" />
              {{ $t("js.election_client.receipt.download_receipt") }}
            </a>
            <template v-if="!election.hide_dbas">
              <div v-if="ballotCode" class="my-3">
                <ScanQr :displayCode="ballotCode" :qrLink="ballotCodeUrl"
                  :codeText="$t('js.election_client.receipt.ballot_code')" />
              </div>
            </template>

            <template v-if="recasting">
              <h5 class="mt-5">{{ $t("js.ballot.redo_header") }}</h5>
              <p>{{ $t("js.ballot.redo_notice") }}</p>
              <button class="btn btn-secondary" @click="startRedoVote">{{ $t("js.ballot.redo_vote") }}</button>
            </template>

            <template v-if="handRaiseLiveResults">
              <hr class="my-5" />
              <HandRaiseLivePreview />
            </template>
          </div>
        </template>

        <!-- Can vote and hasn't already voted -->
        <template v-else>
          <template v-if="!isTesting">
            <template v-if="!confirming">
              <AVSplitHelper
                :image-option="election.theme.imageOption"
                :key="activeContest.reference"
                :contest="formatContest(activeContest)"
                :contestSelection="activeContestSelection"
                :weight="voterWeight"
                :show-submission-helper="false"
                :partial-results="handRaiseLiveResults"
                :includeLazyErrors="includeLazyErrors"
                @view-candidate="viewCandidate"
                @update:contestSelection="updateActiveContestSelection"
                @update:activeState="updateActiveState"
                @update:activePile="updateActivePile"
                @update:complete="updateCompleted" />
              <!-- Continue/Back -->
              <div v-if="showBallotButtons" id="ballot-action-buttons" class="d-flex mt-3 gap-3 flex-column-reverse flex-md-row">
                <button id="ballot_back_button" v-if="contestIndex > 0" class="btn btn-lg btn-ballot-outline col-12 col-md-auto"
                        @click="goBack" v-text="$t('js.election_client.actions.back')" />
                <span class="flex-grow-1"></span>
                <button id="ballot_forward_button" v-if="!isLastContest"
                        :class="`btn btn-lg btn-ballot col-12 col-md-auto`"
                        :disabled="!isActiveContestComplete" @click="goForward"
                        v-text="$t('js.election_client.actions.continue')" />
                <button id="ballot_to_summary_button" v-if="isLastContest && election.showConfirmVotes"
                        :class="`btn btn-lg btn-ballot col-12 col-md-auto`"
                        :disabled="!isBallotComplete" @click="confirming = true"
                        v-text="$t('js.election_client.actions.continue')" />
                <button  id="ballot_to_summary_button" v-if="isBallotPrefilled"
                         :class="`btn btn-lg btn-ballot col-12 col-md-auto`"
                         :disabled="!isBallotComplete" @click="confirming = true"
                         v-text="$t('js.election_client.summary.return_to_summary')"
                />
                <!-- If we dont show confirm page and it is the last contest, this is where we submit the vote -->
                <AsyncButton id="ballot_submit_vote_button" :on-click="attemptSubmitVotes" v-if="!election.showConfirmVotes && isLastContest" :disabled="!isBallotComplete"
                              :style="!isBallotComplete ? { pointerEvents: 'none' } : null"
                              class="btn btn-lg btn-ballot col-12 col-md-auto">
                  {{ $t("js.ballot.submit_vote") }}
                  <template #waiting>
                    <span><i class="fas fa-spin fa-spinner"></i> {{ $t("js.ballot.submitting_vote") }}</span>
                  </template>
                </AsyncButton>
              </div>
            </template>
            <template v-else>
              <BallotSummary
                v-for="contestSelection in ballotSelection.contestSelections"
                :key="contestSelection.reference"
                :contest-selection="contestSelection"
                @goto-selection="(contestReference) => goToContest(contestReference)"
                :is-ballot-prefilled="isBallotPrefilled"
              />

              <!-- Submit/Encrypt/Back -->
              <div id="ballot-action-buttons" class="d-flex mt-3 gap-3 flex-column-reverse flex-md-row">
                <button id="ballot_back_button" class="btn btn-lg btn-ballot-outline col-12 col-md-auto"
                        @click="goBack" v-text="$t('js.election_client.actions.back')" />
                <!-- Clear button goes here -->
                <span class="flex-grow-1"></span>
                <template v-if="!hideBenaloh">
                  <AsyncButton id="ballot_encrypt_vote_button" :class="`btn btn-lg btn-ballot col-12 col-md-auto`"
                               :disabled="!isActiveContestComplete" @click="attemptSubmitVotes">
                    {{ $t("js.election_client.ballot_verification.confirm_choices") }}
                    <template #waiting>
                  <span>
                    <IndeterminateSpinner /> {{ $t("js.ballot.encrypting_vote") }}
                  </span>
                    </template>
                  </AsyncButton>
                </template>

                <div v-else :class="`col-12 col-md-auto`"
                     :data-bs-original-title="!isBallotComplete ? $t('js.ballot.tooltips.ballot_state_incomplete') : null">
                  <AsyncButton id="ballot_submit_vote_button" :on-click="attemptSubmitVotes" :disabled="!isBallotComplete"
                               :style="!isBallotComplete ? { pointerEvents: 'none' } : null"
                               class="btn btn-lg btn-ballot col-12 col-md-auto">
                    {{ $t("js.ballot.submit_vote") }}
                    <template #waiting>
                      <span><i class="fas fa-spin fa-spinner"></i> {{ $t("js.ballot.submitting_vote") }}</span>
                    </template>
                  </AsyncButton>
                </div>
              </div>
            </template>
          </template>
          <template v-else>
            <ChallengeBallot :client="client" :ballot-code="ballotCode" :on-cast-chosen="castVote"
              @go-back="() => { isTesting = false }" />
          </template>

          <div v-if="!election.hideBenaloh" class="my-3 small text-muted hstack justify-content-end gap-1">
            <div class="form-check form-switch m-0">
              <input v-model="benalohOptIn" :disabled="isTesting" class="form-check-input" type="checkbox" role="switch" id="technical-check" />
              <label class="form-check-label" for="technical-check">{{ $t('js.election_client.footer.section3.header') }}</label>
            </div>
            <AVIcon
              icon="info-circle"
              class="cursor-pointer"
              :data-bs-original-title="$t('js.election_client.footer.section3.label')"
              v-tooltip="$t('js.election_client.footer.section3.label')"
            />
          </div>
          <div v-if="handRaiseLiveResults" class="mb-4">
            <hr class="mb-4 mt-3">
            <HandRaiseLivePreview />
          </div>
        </template>
      </template>

      <!-- Cannot vote -->
      <template v-else>
        <BallotProgress :ballot="ballot" class="mt-3 mb-4" />
        <div class="alert alert-info text-center">
          <p class="m-0">
            <AVIcon icon="circle-info" class="me-1" />
            {{ $t("js.ballot.cant_vote") }}
          </p>
        </div>
        <div v-if="votingRoundContests.length === 1">
          <ContestHeader :contest="votingRoundContests[0]" :is-eligible="false" :locale="$i18n.locale"
            :show-description="ballot.state === 'open'" />
          <AVBallot class="mb-5" :contest="formatContest(votingRoundContests[0])" :disabled="true"
            :partial-results="handRaiseLiveResults" :selectionPile="mockSelectionPile" />
          <div v-if="handRaiseLiveResults">
            <hr class="my-5">
            <HandRaiseLivePreview />
          </div>
        </div>
        <div v-else>
          <ContestHeader v-for="contest in votingRoundContests" :contest="contest"
            :show-description="ballot.state === 'open'" :is-eligible="false" :locale="$i18n.locale" />
        </div>
      </template>
    </template>
  </div>

  <!-- Candidate modal -->
  <div
    id="candidatePreviewModal"
    class="modal fade"
    tabindex="-1"
    :aria-labelledby="$t('js.election_client.ballot.view_candidate.candidate_profile')"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down modal-xl candidate-modal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :aria-label="$t('js.actions.close')"
          ></button>
        </div>
        <div class="modal-body p-0">
          <div v-if="candidateData" class="vstack">
            <div class="p-5 bg-light">
              <AVResourceSection :candidate="candidateData" summary />
            </div>
            <div class="p-5">
              <AVResourceSection :candidate="candidateData" />
              <AVRecommendationList
                v-if="candidateData.recommendations"
                :recommendations="candidateData.recommendations"
                :recommendationsPublic="recommendationPublic"
              />
            </div>
          </div>

          <div v-else class="d-flex justify-content-center p-5">
            <AVSpinner color="theme" size="lg" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from "pinia";
import { defineComponent } from "vue";
import Result from "../shared/Result.vue";
import Progress from "@/components/shared/Progress.vue";
import Countdown from "../shared/Countdown.vue";
import ChallengeBallot from "./ChallengeBallot.vue";
import ContestHeader from "@/components/shared/ContestHeader.vue";
import BallotProgress from "../shared/BallotProgress.vue";
import BallotSummary from "./BallotSummary.vue";
import AsyncButton from "@/components/shared/AsyncButton.vue";
import { AVClient } from "@assemblyvoting/js-client";
import ContestSelectionValidator from "@assemblyvoting/js-client/dist/lib/validators/contestSelectionValidator";
import IndeterminateSpinner from "@/components/backend/live/IndeterminateSpinner.vue";
import ScanQr from "@/components/frontend/voting/ScanQr.vue";
import HandRaiseLivePreview from "../shared/HandRaiseLivePreview.vue";
import { formatContest, orderContests, orderResults } from "@/entrypoints/shared/contest_utilities";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import { useVotingSessionStore } from "@/entrypoints/stores/voting_session";
import { useHandRaiseStore } from "@/entrypoints/stores/hand_raise";
import { Modal } from "@assemblyvoting/electa-ui";
import apiClient from "@/entrypoints/frontend/apiClient";
import type {
  PropType,
  ConferenceContest,
  ContestSelection,
  BallotSelection,
  SplitVotingState,
  SelectionPile,
  ConferenceVotingRound,
  ConferenceVotingRoundReport,
  HandRaiseOrchestrator,
} from "@/types";
import { LoginsApiClient } from "@/lib/voting/loginsApiClient";

export default defineComponent({
  inject: ["stickyAlertsSubscribe", "stickyAlertsUnsubscribe"],
  components: {
    IndeterminateSpinner,
    AsyncButton,
    BallotProgress,
    BallotSummary,
    ChallengeBallot,
    Option,
    Countdown,
    Result,
    Progress,
    ScanQr,
    ContestHeader,
    HandRaiseLivePreview,
  },
  props: {
    ballot: {
      type: Object as PropType<ConferenceContest>,
      required: true,
    },
  },
  data() {
    return {
      isActiveContestComplete: false,
      includeLazyErrors: false,
      ballotSelection: null,
      receipt: null,
      redoVote: false,
      client: null,
      contestIndex: 0,
      isTesting: false,
      ballotCode: "",
      confirming: false,
      activeState: "ballot",
      activePile: null,
      benalohOptIn: false,
      optionUnselectable: false,
      mockSelectionPile: {
        multiplier: 1,
        optionSelections: [],
        explicitBlank: false,
      },
      isRtl: false,
      modal: null,
      candidateData: null,
      recommendationPublic: "private",
    }
  },
  computed: {
    ...mapState(useSharedStore, ["election", "electionUrl"]),
    ...mapState(usePresentationStore, ["contests", "votingRounds", "votingRoundReports", "latestConfig"]),
    ...mapState(useVotingSessionStore, ["voter", "authenticateWith", "voterSessionToken"]),
    ...mapState(useHandRaiseStore, ["handRaiseLiveResults"]),
    orchestrators() {
      if (!!this.votingRoundReport) {
        const filteredResults = this.votingRoundReport.resultOrchestrators?.filter((res: HandRaiseOrchestrator) =>
          this.votingRound.contestReferences.includes(res.contestReference));
        return this.orderResults(filteredResults, this.votingRound.contestPositions);
      } else return [];
    },
    title() {
      return this.ballot.title[this.$i18n.locale];
    },
    description() {
      return this.ballot.description[this.$i18n.locale];
    },
    accessibleContests() {
      const accessibleContests = this.contests.filter((contest: ConferenceContest) =>
        this.voter.canVoteOn.includes(contest.reference)
        && this.votingRound.contestReferences.includes(contest.reference)
      );
      return this.orderContests(accessibleContests, this.votingRound.contestPositions);
    },
    votingRoundContests() {
      const vrContests = this.contests.filter((contest: ConferenceContest) =>
        this.votingRound.contestReferences.includes(contest.reference));
      return this.orderContests(vrContests, this.votingRound.contestPositions);
    },
    votingRoundReport() {
      return this.votingRoundReports?.find((rep: ConferenceVotingRoundReport) =>
        rep.votingRoundReference === this.votingRound.reference);
    },
    ballotCodeUrl() {
      return `${this.election.dbasUrl}/${this.$i18n.locale}/${this.election.organisationSlug}/${this.election.slug}/track/${this.ballotCode}`;
    },
    receiptUrl() {
      if (!this.receipt) return "";
      const url = new URL(`${this.latestConfig.items.voterAuthorizerConfig.content.voterAuthorizer.url}/${this.$i18n.locale}/download_receipt`);
      const params = url.searchParams;
      params.set("receipt", this.receipt.receipt);
      params.set("ballotCode", this.ballotCode);
      params.set("authorizationSessionId", this.client.getSessionUuid());
      return url.toString();
    },
    contest() {
      return this.accessibleContests[this.contestIndex] || {};
    },
    stickies() {
      const stickies = [];
      if (this.isBallotOpen &&
        !this.confirming &&
        !this.hasVoted &&
        !this.isTesting &&
        !this.hasVoted &&
        this.activePile &&
        this.activeState === "ballot") {
        stickies.push({
          type: "contestStatus",
          contest: this.formatContest(this.contest),
          activeSelectionPile: this.activePile,
          includeLazyErrors: this.includeLazyErrors,
        });
      }
      if (this.ballot) {
        stickies.push({
          type: "ballotStatus",
          ballot: this.ballot,
          voted: this.hasVoted,
          canVote: this.hasVotingAccess,
        });
      }
      return stickies;
    },
    isBallotComplete() {
      return this.ballotSelection.contestSelections.every((contestSelection: ContestSelection) => {
        const contestContent = this.accessibleContests.find((c: ConferenceContest) =>
          c.reference === contestSelection.reference);
        if (!contestContent) return false;
        return new ContestSelectionValidator({
          contest: this.formatContest(contestContent),
          voterWeight: contestContent.disregardWeights ? 1 : this.voter.weight,
        }).isComplete(contestSelection) && (this.isActiveContestComplete || this.isBallotPrefilled);
      });
    },
    voterWeight() {
      return this.contest.disregardWeights ? 1 : this.voter.weight;
    },
    activeContestSelection() {
      return this.ballotSelection.contestSelections[this.contestIndex];
    },
    activeContest() {
      this.setDisplayScrollToBottom(this.accessibleContests[this.contestIndex].displayScrollToBtmBtn);
      return this.accessibleContests[this.contestIndex];
    },
    votingRound(): ConferenceVotingRound | undefined {
      return this.votingRounds.find((vr: ConferenceVotingRound) => vr.reference === this.ballot.votingRoundReference);
    },
    isIdentifiable() {
      return this.votingRound.identifiable;
    },
    isHandRaise() {
      return this.ballot.handRaise;
    },
    hasVoted() {
      if (this.redoVote) return false;
      return this.voter.votedOn?.includes(this.votingRound.reference);
    },
    isLastContest() {
      return (this.contestIndex + 1) === this.accessibleContests.length;
    },
    isBallotOpen() {
      const openStates = ["open", "countdown"];
      return openStates.includes(this.ballot.state);
    },
    hasVotingAccess() {
      return this.accessibleContests.length > 0;
    },
    hideBenaloh() {
      return this.election.hideBenaloh || !this.benalohOptIn;
    },
    recasting() {
      return this.votingRound.recasting;
    },
    resultPublished() {
      return this.ballot.state === "finished" && !!this.votingRoundReport?.publishedAt;
    },
    showSupplementaryHeader() {
      const enabledBallotStates = ["new", "closed", "calculating_result"];
      return (enabledBallotStates.includes(this.ballot.state)
        || (this.ballot.state === "finished" && !this.votingRoundReport?.publishedAt));
    },
    canDownloadReceipt() {
      return this.election.downloadVotingReceipt;
    },
    userCanSplit() {
      return this.formatContest(this.activeContest).markingType.maxPiles !== 1 && this.voterWeight > 1;
    },
    showBallotButtons() {
      return !this.userCanSplit || this.activeState === "overview";
    },
    prefilledBallotSelection(): BallotSelection | undefined {
      if (!this.votingRound?.prefilledBallotSelection) { return }

      return this.votingRound.prefilledBallotSelection;
    },
    isBallotPrefilled(): boolean {
      return this.prefilledBallotSelection?.contestSelections?.some(cs => cs.piles && cs.piles.length > 0);
    },
  },
  methods: {
    ...mapActions(useVotingSessionStore, ["fetchVotedOn", "reportVoted", "setVoted"]),
    ...mapActions(usePresentationStore, ["setDisplayScrollToBottom"]),
    formatContest,
    orderContests,
    orderResults,
    goToContest(contestReference: string) {
      this.includeLazyErrors = false;
      this.confirming = false;
      let contestIndex = this.ballotSelection.contestSelections.findIndex(cs => cs.reference === contestReference);
      if (contestIndex === -1)
        return console.warn(`tried to go to contest with reference: ${contestReference}, but couldn't find it`)
      this.contestIndex = contestIndex;
      document.getElementById("MainContent").scrollTo({ top: 0, left: 0, behavior: "smooth" });
    },
    goBack() {
      this.includeLazyErrors = false;
      if (this.confirming) {
        this.confirming = false;
        this.contestIndex = this.ballotSelection.contestSelections.length - 1;
      } else this.contestIndex--;
      document.getElementById("MainContent").scrollTo({ top: 0, left: 0, behavior: "smooth" });
    },
    goForward() {
      this.includeLazyErrors = false;
      this.contestIndex++;
      document.getElementById("MainContent").scrollTo({ top: 0, left: 0, behavior: "smooth" });
    },
    goToSummary() {
      if(!this.isBallotComplete) return;
      this.confirming = true;
      this.contestIndex = this.ballotSelection.contestSelections.length - 1;
    },
    async attemptSubmitVotes() {
      const client = new AVClient(this.election.boardUrl, this.election.dbbPublicKey);
      this.client = client;
      await client.initialize();
      if (this.authenticateWith.electionCodes) {
        client.generateProofOfElectionCodes(this.authenticateWith.electionCodes);
      } else if(this.authenticateWith.loginsClient) {
        const identityToken = await new LoginsApiClient(this.authenticateWith.loginsClient).requestIdentityToken();
        client.setIdentityToken(identityToken);
      }
      await client.createVoterRegistration(this.votingRound.reference);
      this.ballotCode = await client.constructBallot(this.ballotSelection);
      if (this.hideBenaloh) {
        await this.castVote();
      } else {
        this.isTesting = true;
      }
    },
    async castVote() {
      this.receipt = await this.client.castBallot(this.$i18n.locale);
      this.isTesting = false;
      this.redoVote = false;
      this.confirming = false;
      this.reportVoted(this.ballot.id); // Just a ping, shouldn't await response

      // Global update without calling the DBB
      this.setVoted(this.votingRound.reference);
    },
    startRedoVote() {
      this.contestIndex = 0;
      this.redoVote = true;
      this.ballotCode = "";
      const selectionPiles = this.ballotSelection.contestSelections[this.contestIndex].piles
      const UnselectableOptions = this.contest.options.map((option) => {
        if (!option.selectable) {
          return option.reference
        }
      })
      if(selectionPiles.length > 0 ) {
        selectionPiles.filter((pile: any) => pile.optionSelections.filter((option: any) => {
          if (UnselectableOptions.includes(option.reference)) {
            return pile.optionSelections = []
          }
        }))
      }
    },
    updateActiveContestSelection(contestSelection: ContestSelection) {
      this.ballotSelection.contestSelections.splice(
        this.contestIndex,
        1,
        contestSelection,
      );
    },
    updateActiveState(newState: SplitVotingState) {
      this.activeState = newState;
    },
    updateActivePile(newPile: SelectionPile) {
      this.activePile = newPile;
    },
    updateCompleted(completed: boolean) {
      if (completed) this.includeLazyErrors = true;
      this.isActiveContestComplete = completed;
    },
    async viewCandidate(contestReference: string, optionReference: string) {
      this.modal.show();
      try {
        const response = await apiClient.get(`${this.electionUrl}/candidate`, {
          headers: {
            Authorization: `Bearer ${this.voterSessionToken}`,
          },
          params: {
            contestReference: contestReference,
            optionReference: optionReference,
          },
        });
        const data = await response.data;
        this.candidateData = data.candidate;
        this.recommendationPublic = data.recommendationsPublic;
      } catch (error) {
        console.error(error);
      }
    },
    resetBallotSelection(includePrefilled: boolean) {
      this.ballotSelection = {
        reference: this.voter.voterGroupReference,
        contestSelections: this.accessibleContests.map((contest: ConferenceContest) => {
          let piles = [];
          if (includePrefilled && this.prefilledBallotSelection) {
            let pile: SelectionPile | undefined = this.prefilledBallotSelection
              .contestSelections.find(cs => cs.reference === contest.reference)?.piles?.[0];
            if (pile) {
              pile = { ...pile };
              pile.multiplier = contest.disregardVoterWeight ? 1 : this.voterWeight;
              piles = [pile];
            }
          }
          return {
            reference: contest.reference,
            piles,
          }
        }),
      }
    },
  },

  watch: {
    "$i18n.locale": function() {
      this.isRtl = document.getElementsByTagName("html")[0].dir === "rtl"
    },
    votingRound: function(newVal, oldVal) {
      if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
      this.resetBallotSelection(true);

      if (this.isBallotComplete) { this.confirming = true } // Only possible if prefilled
    }
  },
  created() {
    this.resetBallotSelection(true);

    this.isRtl = document.getElementsByTagName("html")[0].dir === "rtl"
  },
  mounted() {
    if (this.isBallotComplete) { this.confirming = true }
    const candidateModal = document.querySelector("#candidatePreviewModal");
    if (candidateModal) {
      this.modal = new Modal("#candidatePreviewModal");
      candidateModal.addEventListener("hidden.bs.modal", (): void => (this.candidateData = null));
    }
  },
  async beforeMount() {
    await this.fetchVotedOn();
    this.stickyAlertsSubscribe(this);
  },
  beforeUnmount() {
    this.stickyAlertsUnsubscribe(this);
    this.modal.hide();
  },
});
</script>

<style>
.contest-title {
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
  line-height: 2rem;
  --tw-text-opacity: 1;
  color: rgb(17 24 39);
  font-weight: 500;
}
</style>